import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Badge,
    Box,
    Text,
    Flex,
    Icon,
    Tooltip,
} from "@chakra-ui/react"
import { useState } from "react"
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa"
import LoadingDots from "../UI/LoadingDots"

interface ColumnDefinition<T> {
    title: string
    dataIndex: keyof T
    key?: string
    width?: string | number
    align?: 'left' | 'right' | 'center'
    render?: (value: any, record: T, index: number) => React.ReactNode
    onCell?: (record: T, index: number) => React.HTMLAttributes<HTMLElement>
    sorter?: boolean
    emptyText?: boolean
    sortFunction?: (a: T, b: T) => number
    tooltipMessage?: string
    rowAction?: (record: T, index: number) => React.ReactNode
}

interface DynamicTableProps<T> {
    data: T[]
    columns: ColumnDefinition<T>[]
    loading?: boolean
    rowKey?: (record: T) => string
    onRow?: (record: T, index: number) => React.HTMLAttributes<HTMLElement>
    locale?: {
      emptyText?: string
    }
}

function DynamicTable<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  rowKey,
  onRow,
  locale = { emptyText: "Sem dados" }
}: DynamicTableProps<T>) {
  const [sortConfig, setSortConfig] = useState<{
    key: keyof T | null;
    direction: 'asc' | 'desc' | null;
  }>({
    key: null,
    direction: null
  });

  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  const handleSort = (column: ColumnDefinition<T>) => {
    if (!column.sorter) return;

    let direction: 'asc' | 'desc' | null = 'asc';
    
    if (sortConfig.key === column.dataIndex) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc';
      } else if (sortConfig.direction === 'desc') {
        direction = null;
      }
    }

    setSortConfig({
      key: direction ? column.dataIndex : null,
      direction
    });
  };

  const getSortIcon = (column: ColumnDefinition<T>) => {
    if (!column.sorter) return null;

    if (sortConfig.key === column.dataIndex) {
      if (sortConfig.direction === 'asc') {
        return <Icon as={FaSortUp} ml={2} />;
      }
      if (sortConfig.direction === 'desc') {
        return <Icon as={FaSortDown} ml={2} />;
      }
    }
    return <Icon as={FaSort} ml={2} opacity={0.3} />;
  };

  const sortedData = [...(data || [])].sort((a, b) => {
    if (!sortConfig.key || !sortConfig.direction) return 0;

    const column = columns.find(col => col.dataIndex === sortConfig.key);
    
    if (column?.sortFunction) {
      return sortConfig.direction === 'asc' 
        ? column.sortFunction(a, b)
        : column.sortFunction(b, a);
    }

    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return sortConfig.direction === 'asc'
      ? (aValue > bValue ? 1 : -1)
      : (bValue > aValue ? 1 : -1);
  });

  if (loading) {
    return (
      <Box 
        border="1px" 
        borderColor="gray.50" 
        borderRadius="md" 
        p={4}
        display="flex"
        justifyContent="center"
        alignItems="center"
        minH="80px"
        boxShadow="md"
      >
        <LoadingDots />
      </Box>
    );
  }

  if (!data?.length) {
    return (
      <Flex 
        border="1px" 
        borderColor="gray.50" 
        borderRadius="md" 
        minH="80px"
        boxShadow="md"
        justifyContent="center"
        alignItems="center"
      >
        <Text textAlign="center">{locale.emptyText}</Text>
      </Flex>
    );
  }

  return (
    <Box 
      borderRadius="md" 
      overflow="hidden"
      boxShadow="md"
    >
      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              {columns.map((column, index) => (
                <Th 
                  key={column.key || column.dataIndex as string || index}
                  textAlign={column.align}
                  width={column.width}
                  cursor={column.sorter ? "pointer" : "default"}
                  onClick={() => handleSort(column)}
                  userSelect="none"
                >
                  <Flex alignItems="center">
                    {column.tooltipMessage ? (
                      <Tooltip label={column.tooltipMessage}>
                        <Text fontSize="14px" fontWeight="500">{column.title}</Text>
                      </Tooltip>
                    ) : (
                      <Text fontSize="14px" fontWeight="500">{column.title}</Text>
                    )}
                    {getSortIcon(column)}
                  </Flex>
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {sortedData.map((row, rowIndex) => (
              <Tr 
                key={rowKey ? rowKey(row) : rowIndex}
                cursor="pointer"
                _hover={{ bg: "gray.50" }}
                onMouseEnter={() => setHoveredRow(rowIndex)}
                onMouseLeave={() => setHoveredRow(null)}
                {...(onRow ? onRow(row, rowIndex) : {})}
              >
                {columns.map((column, colIndex) => (
                  <Td 
                    key={column.key || column.dataIndex as string || colIndex}
                    textAlign={column.align}
                    position="relative"
                    {...(column.onCell ? column.onCell(row, rowIndex) : {})}
                  >
                    {column.rowAction ? (
                      <Box
                        visibility={hoveredRow === rowIndex ? "visible" : "hidden"}
                      >
                        {column.rowAction(row, rowIndex)}
                      </Box>
                    ) : (
                      column.render 
                        ? column.render(row[column.dataIndex], row, rowIndex)
                        : row[column.dataIndex]?.toString() || column.emptyText ? '' : '-'
                    )}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export { DynamicTable }